import React from 'react';
import SimpleMarkdown from 'simple-markdown';
import type {Capture, SingleASTNode} from 'simple-markdown';
import makeConstants from 'lib/makeConstants';

const mathJaxOrder = SimpleMarkdown.defaultRules.paragraph.order + 0.5;
const mathJaxFallbackDelay = 1000;
const mathJaxLoadRetryDelay = 50;
const initialRenderSelector = '.markdown-renderer-v2[data-mathjax-initial-render]';
const pendingAttribute = 'data-mathjax-pending';
const pendingElements = new Map<HTMLElement, number>();

let nextTypesetId = 0;
let scheduledTypesetFrame: number | null = null;
let scheduledTypesetRetry: number | null = null;
let mathJaxWaitStartedAt: number | null = null;

const revealTypesetElements = (batch: Array<[HTMLElement, number]>) => {
  const renderers = new Set<HTMLElement>();

  batch.forEach(([element, typesetId]) => {
    if (element.getAttribute(pendingAttribute) === String(typesetId)) {
      element.removeAttribute(pendingAttribute);
    }

    let renderer = element.closest<HTMLElement>(initialRenderSelector);
    while (renderer) {
      renderers.add(renderer);
      renderer = renderer.parentElement?.closest<HTMLElement>(initialRenderSelector) ?? null;
    }
  });

  renderers.forEach((renderer) => {
    if (!renderer.querySelector(`[${pendingAttribute}]`)) {
      renderer.removeAttribute('data-mathjax-initial-render');
    }
  });
};

const typesetPendingElements = () => {
  scheduledTypesetFrame = null;
  scheduledTypesetRetry = null;

  if (pendingElements.size === 0) {
    mathJaxWaitStartedAt = null;
    return;
  }

  const hub = window.MathJax?.Hub;

  if (!hub) {
    // The MathJax script hasn't loaded yet (skipStartupTypeset is on, so nothing else will
    // typeset for us). Past the fallback delay, fail open by revealing the authored source, but
    // keep the batch queued so it is still typeset in place once the script arrives.
    const waitTime = mathJaxWaitStartedAt === null ? 0 : Date.now() - mathJaxWaitStartedAt;
    if (waitTime >= mathJaxFallbackDelay) {
      revealTypesetElements(Array.from(pendingElements.entries()));
    }
    scheduledTypesetRetry = window.setTimeout(typesetPendingElements, mathJaxLoadRetryDelay);
    return;
  }

  const batch = Array.from(pendingElements.entries()).filter(([element]) => element.isConnected);
  pendingElements.clear();
  mathJaxWaitStartedAt = null;

  if (batch.length === 0) {
    return;
  }

  const fallbackTimeout = window.setTimeout(
    () => revealTypesetElements(batch),
    mathJaxFallbackDelay
  );
  const revealBatch = () => {
    window.clearTimeout(fallbackTimeout);
    revealTypesetElements(batch);
  };

  try {
    hub.Queue(['Typeset', hub, batch.map(([element]) => element)], revealBatch);
  } catch (error) {
    revealBatch();
    logger.error('Failed to typeset MathJax content: %o', error);
  }
};

const queueTypeset = (element: HTMLElement | null) => {
  if (!element) {
    return;
  }

  nextTypesetId += 1;
  element.setAttribute(pendingAttribute, String(nextTypesetId));
  pendingElements.set(element, nextTypesetId);
  mathJaxWaitStartedAt ??= Date.now();

  if (scheduledTypesetFrame === null && scheduledTypesetRetry === null) {
    scheduledTypesetFrame = window.requestAnimationFrame(typesetPendingElements);
  }
};

export const dollarEscapeRule = {
  order: mathJaxOrder - 0.1,
  match: (source: string) => /^\\\$/.exec(source),
  parse: () => ({content: null}),
  react() {
    return <span>&#36;</span>;
  },
  html: () => '<span>&#36;</span>'
};

const mathJaxFormats = makeConstants('block', 'inline');

export const mathJaxRule = {
  order: mathJaxOrder,
  match: (source: string) => {
    return /^(\${1,2}[^]*?[^\\]\${1,2})/.exec(source);
  },
  parse: (capture: Capture) => {
    const [content] = capture;
    return {
      content,
      format: content.startsWith('$$') ? mathJaxFormats.block : mathJaxFormats.inline
    };
  },
  react(node: SingleASTNode) {
    return <MathJaxElement format={node.format} content={node.content} />;
  },
  html: (node: SingleASTNode) => node.content
};

type MathJaxElementProps = {
  format: 'block' | 'inline';
  content: string;
};

// MathJax V2
export class MathJaxElement extends React.Component<MathJaxElementProps> {
  state = {
    isFlushing: false
  };

  componentDidMount() {
    queueTypeset(this.ref.current);
  }

  componentDidUpdate(prevProps: MathJaxElementProps) {
    if (prevProps.content !== this.props.content) {
      this.flush().then(this.typesetCurrentNode);
    }
  }

  ref = React.createRef<HTMLDivElement>();

  /**
   * In certain cases, we need to ensure the MarkdownRendererV2's contents are properly cleaned up.
   * For instance, MathJax sometimes causes problems where the MathJax DOM nodes stick around even though
   * the text content has changed. In these cases, we'll "flush" the node (render an empty div) so that any stuck
   * DOM nodes go away before rendering the actual content.
   */
  flush() {
    return new Promise((res) => {
      this.setState(
        {
          isFlushing: true
        },
        () =>
          this.setState(
            {
              isFlushing: false
            },
            () => res({})
          )
      );
    });
  }

  typesetCurrentNode = () => {
    queueTypeset(this.ref.current);
  };

  render() {
    const {format, content} = this.props;
    const Wrapper = format === mathJaxFormats.block ? 'div' : 'span';
    const processedContent = this.state.isFlushing ? '' : content;
    return (
      <Wrapper
        ref={this.ref}
        /**
         * @see: http://docs.mathjax.org/en/v2.7-latest/options/preprocessors/tex2jax.html
         */
        className='tex2jax_process'
        data-mathjax-pending=''
      >
        {processedContent}
      </Wrapper>
    );
  }
}

// MathJax V4

// export const MathJaxElement = ({format, content}: MathJaxElementProps) => {
//   const [ready, setReady] = useState(false);
//   const ref = useRef<HTMLDivElement | null>(null);

//   useEffect(() => {
//     const el = ref.current;

//     if (!el) {
//       return;
//     }

//     (async () => {
//       try {
//         await typesetElement(el);
//       } catch (error) {
//         console.error('Error typesetting MathJax element:', error);
//       } finally {
//         setReady(true);
//       }
//     })();
//   }, [content]);

//   const Wrapper = format === 'block' ? 'div' : 'span';

//   return (
//     <Wrapper
//       ref={ref}
//       className={cx('tex2jax_process', {
//         'opacity-100': ready,
//         'opacity-0': !ready
//       })}
//     >
//       {content}
//     </Wrapper>
//   );
// };
